'use client';

import { SystemLoadingIcon as Loader2 } from '@/components/ui/SystemLoading';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { ArrowUp, Lock, Paperclip, Settings2, Square } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/cn';

/** One footer contract for conversation, image, video and skill composers. */
export function ComposerFooter({ children, className }: { children: ReactNode; className?: string }) {
  return <div data-composer-footer className={cn('flex min-w-0 items-center justify-between gap-2', className)}>{children}</div>;
}

export const ComposerMaterialsButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }>(
  function ComposerMaterialsButton({ loading, children, className, ...props }, ref) {
    const t = useTranslations('consultation');
    return <button ref={ref} type="button" aria-label={loading ? t('uploadingMaterials') : t('attachMaterials')} {...props}
      className={cn('inline-flex min-h-9 items-center gap-2 rounded-lg px-2 text-xs text-gray-500 transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4ABDE9] disabled:cursor-not-allowed disabled:opacity-40', className)}>
      {loading ? <Loader2 size={16} className="animate-spin" aria-hidden /> : <Paperclip size={16} aria-hidden />}
      <span data-composer-materials-label>{children ?? (loading ? t('uploadingMaterials') : t('attachMaterials'))}</span>
    </button>;
  },
);

export const ComposerSettingsButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(
  function ComposerSettingsButton({ className, ...props }, ref) {
    const t = useTranslations('chat');
    return <button ref={ref} type="button" aria-label={t('composerSettings')} {...props}
      className={cn('inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl px-3 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50', className)}>
      <Settings2 className="h-4 w-4 shrink-0" aria-hidden />
      <span data-composer-settings-label>{t('composerSettings')}</span>
    </button>;
  },
);

export const ComposerActionButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string; stopping?: boolean; loading?: boolean; locked?: boolean;
}>(function ComposerActionButton({ label, stopping, loading, locked, className, ...props }, ref) {
  return <button ref={ref} type="button" aria-label={label} title={label} {...props}
    className={cn('inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40',
      stopping || locked ? 'bg-gray-900 hover:bg-gray-700' : 'bg-[#4ABDE9] hover:bg-[#36addb]', className)}>
    {loading ? <Loader2 size={19} className="animate-spin" aria-hidden />
      : stopping ? <Square size={15} fill="currentColor" aria-hidden />
      : locked ? <Lock size={18} aria-hidden /> : <ArrowUp size={20} aria-hidden />}
  </button>;
});
